import type {
  LoggerContext,
  LoggerTransport,
  LogMessage,
} from '@/services/logger/types';
import type { Sparte } from '@/features/Global/types';
import type { Fn } from '@vueuse/core';
import { useTimeoutFn } from '@vueuse/core';
import { useGlobalStore } from '@/features/Global/store';
import { useLoggingApi } from '@/services/api/useLoggingApi';
import hashCode from '@/utils/hashCode/hashCode';

interface LokiContext extends Omit<LoggerContext, 'sparte'> {
  sparte?: Sparte;
}

export interface LokiLogMessage {
  timestamp: number;
  berechnungsId?: string;
  context: LokiContext;
  message: string;
  level: string;
}

export class LokiLogTransport implements LoggerTransport {
  private readonly queue: Map<string, LokiLogMessage> = new Map();

  private pendingLoading: {
    isPending: boolean;
    stop: Fn;
  } = {
    isPending: false,
    stop: (): void => undefined,
  };

  handle(log: LogMessage): void {
    if (useGlobalStore().isWebreport) return;

    // clean context because backend will append these information anyway
    const context: LokiContext = { ...log.context };
    delete context.brokerId;
    delete context.sparte;
    delete context.berechnungsId;

    const logMessage = {
      timestamp: log.ts,
      berechnungsId: log.context.berechnungsId,
      context,
      message: log.message,
      level: log.levelName.toLowerCase(),
    };

    const hash = hashCode(logMessage, {
      excludeKeys: (key) => key === 'timestamp',
    });

    if (this.queue.has(hash)) {
      return;
    }

    this.queue.set(hash, {
      timestamp: log.ts,
      berechnungsId: log.context.berechnungsId,
      context,
      message: log.message,
      level: log.levelName.toLowerCase(),
    });

    if (this.pendingLoading.isPending) {
      this.pendingLoading.stop();
    }

    const { isPending, stop, start } = useTimeoutFn(() => {
      if (this.queue.size === 0) {
        return;
      }

      const [, request] = useLoggingApi().logging.log(
        Array.from(this.queue.values()),
      );

      this.queue.clear();

      request.catch((e) => {
        console.error(e);
      });
    }, 1000);

    this.pendingLoading.isPending = isPending.value;
    this.pendingLoading.stop = stop;
    start();
  }
}
